import React, { useEffect, useCallback, useState, useRef } from 'react';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import clsx from 'clsx';
import { MAIN_THEME } from '@styles';
import { IAbstractRecord } from '@models';
import { IListingData, ListingDataItemType } from '@models/listing';
import { useAppSelector } from '@redux/hooks';
import {
  TableSortOrder,
  convertRecordsIntoEnhancedRows,
} from '@utils/table-utils';
import Separator from '../../common/Separator';
import {
  EnhancedTableHeader,
  EnhancedTableHeaderType,
} from '../widgets/shared-with-old';
import EnhancedTableBody from '../widgets/shared-with-old/EnhancedTableBody';
import EnhancedTableHead from '../widgets/shared-with-old/EnhancedTableHead';
import { makeStyles } from 'tss-react/mui';
import {
  IEnhancedMenuItem,
  IListingTableFilter,
  IFilterModel,
  ITableRowSettings,
} from '../types';
import EnhancedTableToolbar from '../ListingTableToolbar';
import { fetchWidgetMetaFromSession } from '@utils/session-storage-utils';
import { LabelDisplayedRowsArgs, TablePagination } from '@mui/material';
import DateService from '@services/DateService';
import { CDN_URL } from '@utils';

export interface IListingTableWithPaginationProps {
  name: string;
  headers: Record<string, EnhancedTableHeader>;
  data: IListingData;
  rowSettings?: ITableRowSettings;
  actions?: IEnhancedMenuItem[];
  inline?: boolean;
  inlineTitle?: string;
  pageContext?: string;
  dropdownActionsNames?: string[];
  defaultOrderByColumn?: string;
  filterableNames?: string[];
  splitCharacter?: string;
  orderByAscendingByDefault?: boolean;
  secondOrderByColumn?: string;
  secondOrderByAscendingByDefault?: boolean;
  disableSelection?: boolean;
  disabledSorting?: boolean;
  context?: IAbstractRecord;
  loader?: boolean;
  forceHideToolbar?: boolean;
  usePagination?: boolean;
  pageSizeOptions?: number[];
  onSelectionChange?: (selectedRows: string[]) => void;
  onPageChange: (
    page: number,
    searchKey?: string,
    rowsPerPage?: number,
    order?: string,
    orderBy?: string,
    filters?: Record<string, IListingTableFilter>
  ) => Promise<void>;
  handleCellValueChanged?(
    rowKey: number,
    property: string,
    value: unknown
  ): void;
}

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    boxShadow: `0px 0px 6px ${MAIN_THEME.color.white86}`,
    margin: '0 auto',
  },
  toolbarPaper: {
    margin: '0 0 1em',
  },
  separator: {
    height: '2px !important',
    margin: '0 !important',
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  header: {
    margin: '0 0 1em',
    display: 'grid',
    gridTemplateAreas: 'title actions',
    gridGap: '1em',
  },
  title: {
    display: 'block',
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.lg,
    fontFamily: MAIN_THEME.font.medium,
    letterSpacing: 0,
    color: MAIN_THEME.color.black,
    fontWeight: 'normal',
    margin: '0 0 17.5px 0',
    gridArea: 'title',
    alignSelf: 'center',
    width: '450px',
  },
  actionsContainer: {
    margin: '0 3px 0 auto',
  },
  action: {
    '& span': {
      margin: '0.5em auto 0',
    },
  },
  button: {
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
  },
  heightAndCenter: {
    height: '500px',
    justifyContent: 'center',
  },
}));

const ListingTableWithPagination: React.FC<
  IListingTableWithPaginationProps
> = ({
  name,
  headers,
  rowSettings,
  data,
  actions = [],
  inline,
  inlineTitle,
  pageContext,
  dropdownActionsNames = [],
  defaultOrderByColumn,
  orderByAscendingByDefault,
  secondOrderByColumn,
  secondOrderByAscendingByDefault,
  filterableNames,
  disableSelection,
  disabledSorting,
  context,
  loader,
  onPageChange,
  handleCellValueChanged,
  pageSizeOptions,
}) => {
  const pageNumber = data.pageNumber - 1;
  const { classes } = useStyles();
  const userInfo = useAppSelector((state) => state.user.info);
  const currentFiltersAndOtherPayloadValues: IFilterModel =
    fetchWidgetMetaFromSession(userInfo, pageContext, name) || {};

  const orderPayloadObjectKey =
    (currentFiltersAndOtherPayloadValues.pagination &&
      Object.keys(currentFiltersAndOtherPayloadValues.pagination)[0]) ||
    '';

  const orderPayloadObject =
    (!isEmpty(orderPayloadObjectKey) &&
      currentFiltersAndOtherPayloadValues.pagination &&
      currentFiltersAndOtherPayloadValues.pagination[orderPayloadObjectKey] &&
      currentFiltersAndOtherPayloadValues.pagination[orderPayloadObjectKey]
        .OrderBy) ||
    {};

  // Flag that filters-based effect checks to decide whether to skip the request.
  const [ignoreFilterEffect, setIgnoreFilterEffect] = useState(false);

  const [order, setOrder] = useState<TableSortOrder>(
    (orderPayloadObject &&
      Object.values(orderPayloadObject).length > 0 &&
      ((
        Object.values(orderPayloadObject)[0] as string
      ).toLowerCase() as TableSortOrder)) ||
      undefined
  );
  const [orderBy, setOrderBy] = useState<string>(
    Object.keys(orderPayloadObject)[0] || ''
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [filtered, setFiltered] = useState(data.pagedItems);
  const [filterValue, setFilterValue] = useState('');
  const [filters, setFilters] = useState<Record<string, IListingTableFilter>>();
  const [forceClearSearchValueState, setForceClearSearchValueState] =
    useState<boolean>(false);
  const [namedFilters, setNamedFilters] = useState<IAbstractRecord>();
  const [selectedRecords, setSelectedRecords] = useState<
    ListingDataItemType<string>
  >({});
  const currentName = useRef<string>();
  let filterDelay: NodeJS.Timeout;

  const getFilterValues = useCallback(
    (filterName: string) => {
      const options: string[] = [];
      if (data.pagedItems.length === 0) {
        return [];
      }
      Object.values(data.pagedItems).forEach((item) => {
        if (!isEmpty(item[filterName]) && !options.includes(item[filterName])) {
          options.push(item[filterName]);
        }
      });
      return options;
    },
    [data.pagedItems]
  );

  useEffect(() => {
    if (!filterableNames) {
      setFilters(undefined);
      setFiltered(data.pagedItems);
      return;
    }

    const updatedFilters = { ...filters } as Record<
      string,
      IListingTableFilter
    >;

    filterableNames.forEach((key) => {
      let options = getFilterValues(key);
      if (headers && headers[key]?.type === EnhancedTableHeaderType.Date) {
        options = options.map((option) => DateService.formatDate(option));
      }

      if (!updatedFilters[key]) {
        updatedFilters[key] = {
          name: key,
          title: headers[key].title || key,
          options,
          selectedValues: [],
        };
      } else {
        updatedFilters[key] = {
          ...updatedFilters[key],
          options,
        };
      }
    });

    if (!isEqual(updatedFilters, filters)) {
      setFilters(updatedFilters);
    }

    setFiltered(data.pagedItems);
  }, [data.pagedItems, filterableNames, getFilterValues, headers, filters]);

  useEffect(() => {
    let filterDelay: NodeJS.Timeout | undefined;

    if (ignoreFilterEffect) {
      // Reset flag, skip calling onPageChange
      setIgnoreFilterEffect(false);
    } else {
      filterDelay = setTimeout(() => {
        void onPageChange(
          data.pageNumber,
          filterValue,
          data.pageSize,
          order,
          orderBy,
          filters
        );
      }, 250);
    }
    return () => {
      if (filterDelay) {
        clearTimeout(filterDelay);
      }
    };
  }, [filterValue, filters]);

  useEffect(() => {
    if (currentName.current !== name && defaultOrderByColumn) {
      currentName.current = name;
      setOrder(orderByAscendingByDefault ? 'asc' : 'desc');
      setOrderBy(defaultOrderByColumn);
    }
  }, [
    defaultOrderByColumn,
    name,
    orderByAscendingByDefault,
    secondOrderByAscendingByDefault,
    secondOrderByColumn,
  ]);

  useEffect(() => {
    const currentFilters: IFilterModel = fetchWidgetMetaFromSession(userInfo);
    if (
      currentFilters != null &&
      currentFilters.namedFilters != null &&
      !isEqual(namedFilters, currentFilters.namedFilters)
    ) {
      setNamedFilters(currentFilters.namedFilters);
      setFilterValue('');
      setForceClearSearchValueState(true);
    } else {
      setForceClearSearchValueState(false);
    }
  }, [namedFilters, userInfo]);

  function handleRequestSort(
    event: React.MouseEvent<unknown>,
    property: string
  ) {
    const isDesc = orderBy === property && order === 'desc';
    const newOrder = isDesc ? 'asc' : 'desc';
    setOrder(newOrder);
    setOrderBy(property);
    void onPageChange(
      data.pageNumber,
      filterValue,
      data.pageSize,
      newOrder,
      property,
      filters
    );
  }

  function handleSelectAllClick(rowName: string, checked: boolean) {
    if (checked) {
      setSelected(Object.keys(filtered));
      handleUpdateSelectedRecords(Object.keys(filtered));
      return;
    }
    setSelected([]);
    handleUpdateSelectedRecords([]);
  }

  function handleClick(rowName: string) {
    let newSelected: string[] = [];
    newSelected = selected.includes(rowName)
      ? selected.filter((name) => name !== rowName) // Deselect rowName if already selected
      : [...selected, rowName]; // Add rowName to selection
    setSelected(newSelected);
    handleUpdateSelectedRecords(newSelected);
  }

  function handleUpdateSelectedRecords(newSelected: string[]) {
    const selectedRecordsTemp = cloneDeep(selectedRecords);
    Object.values(newSelected).forEach((selectedRecordId) => {
      if (selectedRecordsTemp && filtered[selectedRecordId] !== undefined) {
        selectedRecordsTemp[selectedRecordId] = filtered[selectedRecordId];
      }
    });
    setSelectedRecords(selectedRecordsTemp);
  }

  const handleChange = (search: string) => {
    setFilterValue(search);
  };

  const isSelected = (key: string) => selected.indexOf(key) !== -1;

  const rowCount = data.totalCount || 0;
  const emptyRows = data.pageSize - Object.keys(filtered).length;

  const handleFilterChange = (
    filterName: string,
    field: string,
    value: boolean
  ) => {
    if (filters && filters[filterName]) {
      const newFilters = { ...filters };
      const targetFilter = { ...newFilters[filterName] };
      if (value) {
        targetFilter.selectedValues = [...targetFilter.selectedValues, field];
      } else {
        targetFilter.selectedValues = targetFilter.selectedValues.filter(
          (option: string) => option !== field
        );
      }
      newFilters[filterName] = targetFilter;
      setFilters(newFilters);
    }
  };

  const handleFilterClear = () => {
    if (filters) {
      const newFilters = cloneDeep(filters);
      Object.values(newFilters).forEach((f) => {
        f.selectedValues = [];
      });
      setFilters(newFilters);
    }
  };

  const getRowsPerPageOptions = () => {
    return pageSizeOptions || [5, 10, 25];
  };

  const getLabelDisplayedRows = (pagination: LabelDisplayedRowsArgs) => {
    const { from, to, count } = pagination;
    return `${from}-${to} of ${count}`;
  };

  const getRows = () => {
    return convertRecordsIntoEnhancedRows(filtered);
  };

  const handlePageNumberChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    setIgnoreFilterEffect(true);
    void onPageChange(
      page + 1,
      filterValue,
      data.pageSize,
      order,
      orderBy,
      filters
    );
  };

  function handleRowsPerPageChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    const rowsNumber = Number(event.target.value);
    void onPageChange(1, filterValue, rowsNumber, order, orderBy, filters);
  }

  const renderToolbar = () => (
    <EnhancedTableToolbar
      dropdownActionsNames={dropdownActionsNames}
      selected={selected}
      actions={actions}
      data={selectedRecords}
      handleChange={handleChange}
      cdnUrl={CDN_URL}
      title={inlineTitle}
      onFilterClick={handleFilterChange}
      inlineFilters={filters}
      onFilterClear={handleFilterClear}
      context={context}
      forceClearSearchValue={forceClearSearchValueState}
    />
  );

  return (
    <div className={classes.root}>
      {!inline && (
        <Paper className={clsx(classes.paper, classes.toolbarPaper)}>
          {renderToolbar()}
        </Paper>
      )}
      <Paper className={classes.paper}>
        {inline && (
          <>
            {renderToolbar()}
            <Separator className={classes.separator} />
          </>
        )}
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={Object.keys(filtered).length}
              headRows={headers}
              disableSelection={disableSelection}
              disabledSorting={disabledSorting}
              forceDisableSelectAll={Object.keys(selectedRecords).length === 0}
            />
            <EnhancedTableBody
              emptyRows={emptyRows}
              pageSize={data.pageSize}
              handleClick={handleClick}
              headers={headers}
              rowSettings={rowSettings}
              isSelected={isSelected}
              rows={getRows()}
              handleCellValueChanged={handleCellValueChanged}
              disableSelection={disableSelection}
              loader={loader}
            />
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={getRowsPerPageOptions()}
          component="div"
          count={rowCount}
          rowsPerPage={data.pageSize}
          page={pageNumber}
          backIconButtonProps={{ 'aria-label': 'Previous' }}
          nextIconButtonProps={{ 'aria-label': 'Next' }}
          onPageChange={handlePageNumberChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage=""
          labelDisplayedRows={getLabelDisplayedRows}
        />
      </Paper>
    </div>
  );
};

export default ListingTableWithPagination;
